import { safeLocalStorage } from '@/core/safeLocalStorage/safeLocalStorage';
import {
  useAuthService,
  usePreferencesService,
} from '@/layout/appWrapper/ServiceProvider';
import { useUser } from '@/services/userService';
import { authState } from '@/stores/authStore';
import * as Sentry from '@sentry/nextjs';
import { getAuth, signOut } from 'firebase/auth';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useSetRecoilState } from 'recoil';
import { closeModalSelector } from '@/stores/modalStore';

export default function useLoginCallbacks() {
  const router = useRouter();
  const setToken = useSetRecoilState(authState);
  const closeModal = useSetRecoilState(closeModalSelector);
  const { getDefaultPreferences } = usePreferencesService();
  const { mutate: mutateUser } = useUser();
  const {
    loginWithAccount,
    loginAsGuest,
    migrateWebOnboardingUser,
    revokeCurrentSession,
  } = useAuthService();

  const login = useCallback(
    async (firebaseIdToken: string) => {
      const preferences = getDefaultPreferences();
      const user = await loginWithAccount(firebaseIdToken, preferences);
      setToken({ token: user.accessToken, isGuestToken: false });
      await mutateUser();
    },
    [getDefaultPreferences, loginWithAccount, setToken, mutateUser]
  );

  const logout = useCallback(async () => {
    closeModal('user:account');
    closeModal('user:account-modal');

    // TODO remove after closing auto advance experiment
    // to avoid showing the auto advance after logout
    safeLocalStorage.setItem('autoAdvance:shown', 'true');

    try {
      await revokeCurrentSession();
    } catch (error) {
      Sentry.captureException(error);
    }

    const guestToken = await loginAsGuest();
    setToken(guestToken);
    const auth = getAuth();
    await signOut(auth);
    const { pathname } = router;
    delete router.query.mediaType;
    delete router.query.taskId;
    await router.push({
      pathname: '/',
      query: router.query,
    });
    if (pathname === '/result') router.reload();
  }, [closeModal, loginAsGuest, revokeCurrentSession, router, setToken]);

  const migrate = useCallback(
    async (webOnboardingUserId?: string) => {
      await migrateWebOnboardingUser(webOnboardingUserId);
    },
    [migrateWebOnboardingUser]
  );

  return useMemo(
    () => [login, logout, migrate] as const,
    [login, logout, migrate]
  );
}
